import { Container } from '@material-ui/core';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import {
    Cancel as CancelIcon,
    Save as SaveIcon,
} from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import CheckBox from "../../components/Form/Checkbox";
import Select from '../../components/Form/Select';
import SelectSupplier from '../../components/Form/SelectSupplier';
import TextField from '../../components/Form/TextField';
import ButtonUI from '../../components/ui/ButtonUI';
import LoadingUI from '../../components/ui/LoadingUI';
import PageTitle from '../../components/ui/PageTitleUI';
import { useAuth } from '../../hooks/auth';
import { VehiclePost } from '../../interfaces/Vehicle';
import VehicleApi from '../../services/VehicleApi';
import VehicleTypeApi from '../../services/VehicleTypeApi';
import { useStyles } from './Style';
import { UF } from '../../interfaces/City';

type Props = {
    vehicleCode: number,
    clickHandlerCalbkack?: () => void,
    clickHandlerClose?: () => void,
    handleSetSuccess?: (value: boolean) => void,
}

const VehicleForm: React.FC<Props> = (props) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const { t: _t } = useTranslation("General");
    const { t } = useTranslation("Vehicle");
    const { t: tval } = useTranslation("Validation");
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const [UFList, setUFList] = useState<any>([]);
    const [vehicleTypeList, setVehicleTypeList] = useState<any>([]);

    //Campos do Form
    interface IFormPanel {
        supplier: any;
        vehiclePlateNumber: any;
        cartPlateNumber: any;
        vehicleType: any;
        vehicleManufacturer: any;
        renavam: any;
        yearVehicleForce: any;
        ufRegistration: any;
        width: any;
        height: any;
        length: any;
        loadCapacity: any;
        flagEuroClassification: any;
        tareVehicle: any;
        cpF_CNPJ_Owner: any;
        rntcOwner: any;
        corporateNameOwner: any;
        ieOwner: any;
        ufOwner: any;
        flagOwnVehicle: any;
    }

    //Metodos do form:
    const { handleSubmit, reset, control, setError, setValue } = useForm<IFormPanel>({
        defaultValues: {
            supplier: {},
            vehiclePlateNumber: "",
            cartPlateNumber: "",
            vehicleType: {},
            vehicleManufacturer: "",
            renavam: "",
            yearVehicleForce: "",
            ufRegistration: {},
            width: "",
            height: "",
            length: "",
            loadCapacity: "",
            flagEuroClassification: "",
            tareVehicle: "",
            cpF_CNPJ_Owner: "",
            rntcOwner: "",
            corporateNameOwner: "",
            ieOwner: "",
            ufOwner: {},
            flagOwnVehicle: false
        }
    });

    const yupValidationSaveForm = async (data: any) => {

        try {
            const schema = Yup.object().shape({
                vehiclePlateNumber: Yup.string()
                    .length(7, tval("requiredLength7"))
                    .required(tval("requiredField")),
                vehicleType: Yup.object().nullable().required(tval("requiredField"))
                    .shape({code: Yup.string().required(tval("requiredField"))}),
                vehicleManufacturer: Yup.string().required(tval("requiredField")),
            });

            await schema.validate(data, {
                abortEarly: false,
            });

        } catch (err) {
            
            var errors: any[] = []

            if (err instanceof Yup.ValidationError) {
               err.inner.forEach(error => {

                if (errors.filter(element => element.error.includes(error.message)).length === 0)
                    errors.push( {prop: error.path?.split('.')[0], error: error.message})

                setError(error.path?.split('.')[0] as
                    "vehiclePlateNumber" | "vehicleType" | "vehicleManufacturer" 
                    , { message: error.message })
                })

                errors.forEach(element => { 
                    if (element.error === tval("requiredField"))
                        enqueueSnackbar(_t("requiredFieldsValidation"), { variant: 'warning' }) 
                    else    
                        enqueueSnackbar(tval("theField") + t(element.prop) + " " + element.error, { variant: 'warning' }) 
                })
            }
 
            return false
        }

        return true
    }

    const onSubmit = async (data: any) => {
        if(!(await yupValidationSaveForm(data)))
            return;

        //Setar valor dos campos <Select>:
        data.vehicleType = data.vehicleType.code;
        data.supplierId = data.supplier.code;
        data.supplierName = data.supplier.name;
        data.ufOwner = data.ufOwner.code;
        data.ufRegistration = data.ufRegistration.code;
        data.cpF_CNPJ_Owner = data.cpF_CNPJ_Owner ? data.cpF_CNPJ_Owner.replaceAll('.', '').replaceAll('-', '') : '';
        data.companyId = user.company.companyID;
        
        setOpenBackdrop(true);
        if (props.vehicleCode && props.vehicleCode > 0) {

            let put:VehiclePost = data;
            put.id = props.vehicleCode;
            put.updatedBy = user.userName;

            VehicleApi.putVehicle(put)
                .then(result => {
                    enqueueSnackbar(t("returnSubmitEditFormSuccess"), { variant: 'success' });
                    if (props && props.handleSetSuccess)
                        props.handleSetSuccess(true);
                    if (props && props.clickHandlerCalbkack)
                        props.clickHandlerCalbkack()
                }).finally(() => {
                    setOpenBackdrop(false);
                });
        }
        else {

            VehicleApi.postVehicle(data)
                .then(result => {
                    enqueueSnackbar(t("returnSubmitFormSuccess"), { variant: 'success' });
                    if (props && props.handleSetSuccess)
                        props.handleSetSuccess(true);
                    if (props && props.clickHandlerCalbkack)
                        props.clickHandlerCalbkack()
                }).finally(() => {
                    setOpenBackdrop(false);
                });
        }
    }

    const getVehicle = useCallback(
        (vehicleCode: number) => {
            if (props.vehicleCode && props.vehicleCode > 0) {
                setLoading(true);
                VehicleApi.getVehicle(vehicleCode).then((result) => {
                    if (result) {
                        reset({
                            supplier: {code: result.supplierId, label: (result.supplierId != null ? (result.supplierId + " - " + result.supplierName) : ""), name: (result.supplierName ?? "") },
                            vehiclePlateNumber: result.vehiclePlateNumber,
                            cartPlateNumber: result.cartPlateNumber,
                            vehicleType: { code: result.vehicleType, label: result.vehicleTypeDescription },
                            vehicleManufacturer: result.vehicleManufacturer,
                            renavam: result.renavam,
                            yearVehicleForce: result.yearVehicleForce,
                            ufRegistration: { code: result.ufRegistration, label: result.ufRegistration },
                            width: result.width,
                            height: result.height,
                            length: result.length,
                            loadCapacity: result.loadCapacity,
                            flagEuroClassification: result.flagEuroClassification,
                            tareVehicle: result.tareVehicle,
                            cpF_CNPJ_Owner: result.cpF_CNPJ_Owner,
                            rntcOwner: result.rntcOwner,
                            corporateNameOwner: result.corporateNameOwner,
                            ieOwner: result.ieOwner,
                            ufOwner: {code: result.ufOwner, label: result.ufOwner},
                            flagOwnVehicle:result.flagOwnVehicle
                        });
                    }
                }).finally(() => {
                    setLoading(false);
                });
            }
            else {
                reset({
                    supplier: {},
                    vehiclePlateNumber: "",
                    cartPlateNumber: "",
                    vehicleType: {},
                    vehicleManufacturer: "",
                    renavam: "",
                    yearVehicleForce: "",
                    ufRegistration: "",
                    width: "",
                    height: "",
                    length: "",
                    loadCapacity: "",
                    flagEuroClassification: "",
                    tareVehicle: "",
                    cpF_CNPJ_Owner: "",
                    rntcOwner: "",
                    corporateNameOwner: "",
                    ieOwner: "",
                    ufOwner: "",
                    flagOwnVehicle: false
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props]
    );

    useEffect(() => {

        //Evitar pesquisas desnecessarias ao fechar o form:
        if (props.vehicleCode < 0)
            return;

        setUFList(UF.map(
            (uf: any) => ({
                code: uf.SIGLA_UF, label: uf.SIGLA_UF
            })));

        VehicleTypeApi.searchAll()
        .then(response => {
        if (response)
                setVehicleTypeList(response.map(
                (item: any)  => ({
                    code: item.code, label: item.description
                })
                ));
        });

        setLoading(false);
        getVehicle(props.vehicleCode);
    }, [props, getVehicle]);

    return (
        <Container maxWidth={false} component="main">
        {
        loading ?
            <LoadingUI />
            :
            <React.Fragment>

                <Backdrop className={classes.backdrop} open={openBackdrop}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                <form translate={undefined} onSubmit={handleSubmit(onSubmit)} id="formVehicle">
                    <Grid container spacing={1}>

                        <Grid item xs={3}>
                            <PageTitle title={`${props.vehicleCode <= 0 ? t("addTitle") : t("editTitle")}`} style={{ marginLeft: "40px" }} />
                        </Grid>

                        <Grid item xs={9}>
                            <div className={classes.textRight}>
                                <ButtonUI
                                    variant="outlined"
                                    onClick={() => props && props.clickHandlerClose ? props.clickHandlerClose() : null}
                                    startIcon={<CancelIcon />}
                                >
                                    {_t("cancel")}
                                </ButtonUI>

                                <ButtonUI
                                    type="submit"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<SaveIcon />}
                                >
                                    {_t("save")}
                                </ButtonUI>
                            </div>
                        </Grid>
                    </Grid>

                    <Grid container spacing={1}>

                        <Grid item xs={4}>
                            <SelectSupplier 
                                id="supplier" 
                                control={control}
                                name="supplier" multiple={false} style={{  width: '100%' }}
                                setValue={setValue}
                                loadUserSupplier={props.vehicleCode === 0}
                                />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                label={t("vehiclePlateNumber")}
                                name="vehiclePlateNumber"
                                control={control}
                                className={classes.rootForm}
                                mask="aaa9*99"
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                label={t("cartPlateNumber")}
                                name="cartPlateNumber"
                                control={control}
                                className={classes.rootForm}
                                mask="aaa9*99"
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={4}>
                            <Select 
                                name='vehicleType'
                                label={t("vehicleType")}
                                control={control} 
                                options={vehicleTypeList} />
                        </Grid>

                    </Grid>
                    <Grid container spacing={1}>
                        <Grid item xs={2}>
                            <TextField
                                label={t("vehicleManufacturer")}
                                name="vehicleManufacturer"
                                control={control}
                                className={classes.rootForm}
                                fullWidth
                                inputProps={{ maxLength: 30 }}
                                />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                label={t("renavam")}
                                name="renavam"
                                type={"number"}
                                control={control}
                                className={classes.rootForm}
                                fullWidth
                                inputProps={{ maxLength: 13 }}
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                label={t("yearVehicleForce")}
                                name="yearVehicleForce"
                                type={"number"}
                                control={control}
                                className={classes.rootForm}
                                inputProps={{ maxLength: 4 }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <Select 
                                name="ufRegistration" 
                                control={control}
                                options={UFList} 
                                label={t("ufRegistration")}
                                fullWidth
                                />
                        </Grid>

                    </Grid>
                    <Grid container spacing={1}>
                        <Grid item xs={2}>
                            <TextField
                                label={t("width")}
                                name="width"
                                type={"number"}
                                control={control}
                                className={classes.rootForm}
                                inputProps={{ maxLength: 9 }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                label={t("height")}
                                name="height"
                                control={control}
                                type={"number"}
                                className={classes.rootForm}
                                inputProps={{ maxLength: 9 }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                label={t("length")}
                                name="length"
                                control={control}
                                type={"number"}
                                className={classes.rootForm}
                                inputProps={{ maxLength: 9 }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                label={t("loadCapacity")}
                                name="loadCapacity"
                                control={control}
                                type={"number"}
                                className={classes.rootForm}
                                inputProps={{ maxLength: 9 }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                label={t("flagEuroClassification")}
                                name="flagEuroClassification"
                                control={control}
                                className={classes.rootForm}
                                inputProps={{ maxLength: 3 }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                label={t("tareVehicle")}
                                name="tareVehicle"
                                type={"number"}
                                control={control}
                                className={classes.rootForm}
                                inputProps={{ maxLength: 11 }}
                                fullWidth
                            />
                        </Grid>

                    </Grid>

                    <Grid container spacing={1}>

                        <Grid item xs={3}>
                            <TextField
                                label={t("cpF_CNPJ_Owner")}
                                name="cpF_CNPJ_Owner"
                                control={control}
                                className={classes.rootForm}
                                fullWidth
                                mask="999.999.999-99"
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                label={t("rntcOwner")}
                                name="rntcOwner"
                                control={control}
                                fullWidth
                                className={classes.rootForm}
                                inputProps={{ maxLength: 8 }}
                            />
                        </Grid>

                        <Grid item xs={3}>
                            <TextField
                                label={t("corporateNameOwner")}
                                name="corporateNameOwner"
                                control={control}
                                className={classes.rootForm}
                                inputProps={{ maxLength: 60 }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <TextField
                                label={t("ieOwner")}
                                name="ieOwner"
                                control={control}
                                className={classes.rootForm}
                                inputProps={{ maxLength: 11 }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={2} >
                            <Select 
                                name="ufOwner" 
                                control={control}
                                options={UFList} 
                                label={t("ufOwner")}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={1}>

                        <Grid item xs={12}>
                            <CheckBox
                                name="flagOwnVehicle"
                                label={t("flagOwnVehicle")}
                                control={control}
                                color="primary"
                            />
                        
                        </Grid>

                    </Grid>

                </form>

            </React.Fragment>
        }
        </Container>
    )
}
export default VehicleForm;



